import { ApiClientMiddleware } from './api-client-middleware';

export interface AuthHeaderMiddlewareOptions {
  /**
   * Returns the access token to send for a given request URL, or `null` to omit the
   * `Authorization` header. The URL is provided so apps can pick the right token (e.g. a
   * device token vs a user token, or no token at all on `/auth/*` endpoints).
   */
  getAccessToken(url: string): string | null;

  /**
   * Returns the tenant id to send as `X-Tenant-Id`, or `null` to omit it. Defaults to no
   * tenant header when omitted.
   */
  getTenantId?(url: string): string | null;

  /**
   * Substrings: when the request URL contains any of these, the tenant header is suppressed.
   * Used for third-party calls (e.g. external geocoding) that share the same Configuration.
   */
  tenantHeaderExcludeUrls?: readonly string[];

  /**
   * When `true` (the default), sets `Accept: application/json` if the caller did not already
   * set an `Accept` header. Mirrors the historical `HttpClient` default; some endpoints
   * (e.g. SSE streams) override this from the call site.
   */
  setAcceptJson?: boolean;
}

/**
 * Pre-middleware that injects `Authorization`, `X-Tenant-Id`, and (optionally) a default
 * `Accept: application/json` header on every request. URL-aware token selection lets mobile
 * pick device vs user token, and lets every app skip auth on `/auth/*` calls.
 *
 * Headers are normalised through `new Headers(...)` so the middleware handles every
 * `HeadersInit` shape — plain `Record`, `Headers` instance, or `string[][]` — uniformly.
 * Critical for the refresh-retry path: when this middleware re-runs on a retry, the upstream
 * language middleware has already converted headers to a `Headers` instance, and a naive
 * spread (`{ ...headers }`) would silently drop every entry because `Headers` instances
 * expose only methods, not enumerable properties. Using the `Headers` API guarantees that
 * caller-supplied headers like `Content-Type: application/json` survive the round-trip.
 */
export function createAuthHeaderMiddleware(
  options: AuthHeaderMiddlewareOptions
): ApiClientMiddleware {
  const setAcceptJson = options.setAcceptJson ?? true;
  const tenantExcludes = options.tenantHeaderExcludeUrls ?? [];

  return {
    pre: async (context) => {
      const headers = new Headers(context.init.headers);

      if (setAcceptJson && !headers.has('Accept')) {
        headers.set('Accept', 'application/json');
      }

      const token = options.getAccessToken(context.url);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      if (options.getTenantId) {
        const tenantId = options.getTenantId(context.url);
        const isExcluded = tenantExcludes.some((segment) => context.url.includes(segment));
        if (tenantId && !isExcluded) {
          headers.set('X-Tenant-Id', tenantId);
        }
      }

      return { url: context.url, init: { ...context.init, headers } };
    },
  };
}
